/**
 * useSuggest — the rows `GET /wall/suggest?q=` offers for the query in the field (`../suggest.ts`). Debounced; the
 * request in flight is aborted by the next keystroke, and an answer that arrives after it was abandoned is ignored.
 * The last rows stay on screen while the next ones are asked for, so the panel does not blink between keystrokes;
 * `fresh` says whether they answer the query now in the field. A row's `replaces` counts trailing words of the query
 * it answered, so a row taken by Enter must be fresh: taken against a longer query it would eat the words typed since.
 */
import {useEffect, useState} from 'react'

import type {SuggestRow} from '@core/wall/suggest'
import {parseQuery} from '@core/wall/tsQuery'

const DEBOUNCE_MS = 120

const NONE: {q: string; rows: SuggestRow[]} = {q: '', rows: []}

export function useSuggest(q: string, enabled: boolean): {rows: SuggestRow[]; fresh: boolean} {
    const [answer, setAnswer] = useState(NONE)
    const asked = enabled && parseQuery(q).words.trim().length > 0

    useEffect(() => {
        if (!asked) {
            setAnswer(NONE)
            return
        }
        const abort = new AbortController()
        const timer = setTimeout(async () => {
            try {
                const res = await fetch(`/wall/suggest?q=${encodeURIComponent(q)}`, {signal: abort.signal})
                if (!res.ok) return
                const data: unknown = await res.json()
                const rows = (data as {rows?: unknown}).rows
                if (!abort.signal.aborted && Array.isArray(rows)) setAnswer({q, rows: rows as SuggestRow[]})
            } catch {
                // aborted, offline or malformed: the rows stay as they were
            }
        }, DEBOUNCE_MS)
        return () => {
            clearTimeout(timer)
            abort.abort()
        }
    }, [q, asked])

    return asked ? {rows: answer.rows, fresh: answer.q === q} : {rows: [], fresh: true}
}
